import type {
  ExportMode,
  ExportTableCell,
  ExportTableColumn,
  ExportTableModel,
  ExportTableRow,
} from "@cayo/shared/services/export";

export interface GridExportColumnSource {
  key?: string;
  fieldName?: string;
  name?: string;
  currentWidth?: number;
  calculatedWidth?: number;
  isVisible?: boolean;
}

export interface GridExportRowSource {
  id?: string | number;
  key?: string | number;
  [key: string]: unknown;
}

export interface GridDataExportSource<
  TColumn extends GridExportColumnSource = GridExportColumnSource,
  TRow extends GridExportRowSource = GridExportRowSource,
> {
  title?: string;
  generatedAt?: Date;
  columns: TColumn[];
  items: TRow[];
  selectedItems?: TRow[];
}

export interface GridDataExportOptions {
  mode: ExportMode;
}

type ResolvedColumn = ExportTableColumn;

interface GridExportSelectionContext {
  selectedRowKeys: Set<string> | undefined;
}

interface GridExportSelectRowsParams<
  TRow extends GridExportRowSource,
> extends GridExportSelectionContext {
  rows: TRow[];
  mode: ExportMode;
}

interface GridExportBuildRowParams<
  TRow extends GridExportRowSource,
> extends GridExportSelectionContext {
  row: TRow;
  rowIndex: number;
  columns: ResolvedColumn[];
}

export class GridDataExportService {
  buildTable<
    TColumn extends GridExportColumnSource = GridExportColumnSource,
    TRow extends GridExportRowSource = GridExportRowSource,
  >(source: GridDataExportSource<TColumn, TRow>, options: GridDataExportOptions): ExportTableModel {
    const columns = this._selectColumns(source.columns);
    const selection = {
      selectedRowKeys: this._createSelectedRowKeys(source.selectedItems ?? []),
    };
    const rows = this._selectRows({ rows: source.items, mode: options.mode, ...selection }).map(
      (row, index) => this._buildRow({ row, rowIndex: index, columns, ...selection })
    );

    return {
      title: source.title,
      generatedAt: source.generatedAt ?? new Date(),
      columns: columns.map((column) => ({
        key: column.key,
        fieldName: column.fieldName,
        headerText: column.headerText,
        width: column.width,
        align: column.align,
      })),
      rows,
    };
  }

  private _selectColumns<TColumn extends GridExportColumnSource>(
    columns: TColumn[]
  ): ResolvedColumn[] {
    return columns
      .filter((column) => column.isVisible ?? true)
      .map((column, index) => ({
        key: this._getDefaultColumnKey(column, index),
        fieldName: column.fieldName ?? column.key ?? `column-${index}`,
        headerText: column.name ?? column.fieldName ?? column.key ?? `Column ${index + 1}`,
        width: column.currentWidth ?? column.calculatedWidth,
      }));
  }

  private _selectRows<TRow extends GridExportRowSource>({
    rows,
    selectedRowKeys,
    mode,
  }: GridExportSelectRowsParams<TRow>): TRow[] {
    switch (mode) {
      case "selected":
        return rows.filter((row, index) => this._isSelected(row, index, selectedRowKeys));
      case "visible":
      case "all":
      default:
        return rows;
    }
  }

  private _buildRow<TRow extends GridExportRowSource>({
    row,
    rowIndex,
    columns,
    selectedRowKeys,
  }: GridExportBuildRowParams<TRow>): ExportTableRow {
    const cells = columns.map((column) => this._buildCell(row, column));
    const selected = this._isSelected(row, rowIndex, selectedRowKeys);

    return {
      key: this._getDefaultRowKey(row, rowIndex),
      exportable: true,
      selected,
      visible: true,
      cells,
    };
  }

  private _buildCell<TRow extends GridExportRowSource>(
    row: TRow,
    column: ResolvedColumn
  ): ExportTableCell {
    const value = row[column.fieldName as keyof TRow];

    return {
      columnKey: column.key,
      value,
    };
  }

  private _isSelected<TRow extends GridExportRowSource>(
    row: TRow,
    rowIndex: number,
    selectedRowKeys: Set<string> | undefined
  ): boolean {
    if (!selectedRowKeys?.size) {
      return false;
    }

    return selectedRowKeys.has(this._getDefaultRowKey(row, rowIndex));
  }

  private _createSelectedRowKeys<TRow extends GridExportRowSource>(
    selectedItems: TRow[]
  ): Set<string> | undefined {
    if (!selectedItems.length) {
      return undefined;
    }

    return new Set(
      selectedItems.map((selectedRow, index) => this._getDefaultRowKey(selectedRow, index))
    );
  }

  private _getDefaultColumnKey(column: GridExportColumnSource, index: number): string {
    return column.key ?? column.fieldName ?? `column-${index}`;
  }

  private _getDefaultRowKey(row: GridExportRowSource, index: number): string {
    return `${row.id ?? row.key ?? index}`;
  }
}

const gridDataExportService = new GridDataExportService();

export default gridDataExportService;
